<template>
    <div class="RadioInput" v-bind="plainAttrs">
        <label class="RadioInput__label">
            <input
                class="RadioInput__field"
                type="radio"
                :name="name"
                :value="value"
                :checked="value === modelValue"
                :disabled="disabled"
                v-on="listeners"
            >
            <span class="RadioInput__text">{{ label }}</span>
        </label>
    </div>
</template>

<style lang="scss">
@import "../style-variables.scss";

.RadioInput {
    width: 100%;

    &__label {
        @include selection-label;
    }

    &__field {
        @include selection-field-base;
        border-radius: 50%;

        &:checked {
            border-color: var(--eidTrustGreen);
            background-color: var(--eidWhite);

            &::after {
                content: "";
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 0.5rem;
                height: 0.5rem;
                border-radius: 50%;
                background-color: var(--eidQualityBlack);
            }
        }
    }

    &__text {
        @include selection-text;

        .RadioInput__field:disabled + & {
            color: var(--eidTechGray-50);
        }
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { omitListenerAttrs, toEventListeners } from '@evidentid/vue-commons/utils/attrsListeners';

    export default defineComponent({
        name: 'RadioInput',
        inheritAttrs: false,
        props: {
            label: {
                type: String as PropType<string>,
                default: '',
            },
            name: {
                type: String as PropType<string>,
                required: true,
            },
            value: {
                type: [ String, Boolean ] as PropType<string | boolean>,
                default: '',
            },
            modelValue: {
                type: [ String, Boolean ] as PropType<string | boolean>,
                default: '',
            },
            disabled: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        computed: {
            // The handlers are bound to the inner element by `v-on="listeners"`. With
            // inheritAttrs left on, Vue 3 would bind them to this component's root as
            // well, and a native event bubbling out of the inner element would call the
            // caller twice -- which silently undoes a toggle. Ordinary attributes still
            // reach the root through `plainAttrs`.
            plainAttrs(): Record<string, unknown> {
                return omitListenerAttrs(this.$attrs);
            },
            // PRODUCT-26110 Task 10 (INSTANCE_LISTENERS): Vue 3 removed the separate
            // `$listeners` object -- parent-supplied event handlers now arrive merged
            // into `$attrs` as `onEventName`. Rebuild the bare-event-name shape
            // `v-on="listeners"` expects.
            listeners(): Record<string, (...args: unknown[]) => void> {
                return toEventListeners(this.$attrs);
            },
        },
    });
</script>
